import styled from 'styled-components';
import PropTypes from 'prop-types';
import Logo from './Logo';
import Navigation from './Navigation';
import { useTranslation } from 'react-i18next';

const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  overflow: visible;
  justify-content: space-between;
  padding: 0 2rem;
  height: 100px;
  max-height: 100px;
  background: #fffdf0;
  box-shadow: 0 10px 15px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 100px) {
    padding: 0 1rem;
    height: 56px;
  }
`;

import gbFlag from '../assets/flags/gb.svg';
import grFlag from '../assets/flags/gr.svg';
import esFlag from '../assets/flags/es.svg';
import deFlag from '../assets/flags/de.svg';

const LangSwitcher = styled.div`
  position: relative;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  z-index: 10;

  .dropdown-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.1rem 0.2rem;
    border-radius: 6px;
    outline: none;
    display: flex;
    align-items: center;
    transition: box-shadow 0.15s, background 0.15s;
    &:focus, &.open {
      box-shadow: 0 0 0 2px #dc8861;
      background: #f4e7d8;
    }
    &:hover {
      background: #f4e7d8;
    }
  }
  .arrow {
    margin-left: 0.3rem;
    font-size: 0.9rem;
    color: #0d3462;
    pointer-events: none;
  }
  .dropdown-menu {
    position: absolute;
    top: 110%;
    left: 0;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    border-radius: 8px;
    min-width: 40px;
    padding: 0.2rem 0.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    z-index: 100;
  }
  .dropdown-item {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.15rem 0.3rem;
    border-radius: 6px;
    display: flex;
    align-items: center;
    font-size: 1rem;
    transition: background 0.15s;
    &:hover, &:focus, &.active {
      background: #f4e7d8;
    }
  }
  img {
    width: 28px;
    height: 20px;
    display: block;
  }
`;

import React, { useState, useRef, useEffect } from 'react';

const LANGS = [
  { code: 'en', flag: gbFlag },
  { code: 'gr', flag: grFlag },
  { code: 'es', flag: esFlag },
  { code: 'de', flag: deFlag }
];

const Header = ({ navLinks }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();

  const handleLangChange = lang => {
    if (currentLang !== lang) i18n.changeLanguage(lang);
    setOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const current = LANGS.find(l => l.code === currentLang);
  const otherLangs = LANGS.filter(l => l.code !== currentLang);

  return (
    <HeaderContainer>
      <Logo />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Navigation links={navLinks} />
        <LangSwitcher ref={dropdownRef}>
          <button
            className={`dropdown-btn${open ? ' open' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label="Select language"
            title="Select language"
            onClick={() => setOpen(o => !o)}
          >
            <img src={current.flag} alt="Current language flag" />
            <span className="arrow" aria-hidden="true">▼</span>
          </button>
          {open && (
            <div className="dropdown-menu" role="listbox">
              {otherLangs.map(lang => (
                <button
                  key={lang.code}
                  className="dropdown-item"
                  onClick={() => handleLangChange(lang.code)}
                  aria-selected={false}
                  role="option"
                  tabIndex={0}
                >
                  <img src={lang.flag} alt="Language flag" />
                </button>
              ))}
            </div>
          )}
        </LangSwitcher>
      </div>
    </HeaderContainer>
  );
};

Header.propTypes = {
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default Header;
